(() => {
  if (customElements.get('bike-comparison')) return;

  customElements.define('bike-comparison', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.stopStickyHeader?.();
      this.stopDragScroll?.();
      this.controller = new AbortController();
      this.setupStickyHeader();
      this.setupDragScroll();
      const choices = [...this.querySelectorAll('[data-bike-choice]')];
      const controls = this.querySelector('[data-compare-controls]');
      const differences = this.querySelector('[data-compare-differences]');
      if (!controls || choices.length < 2) return;

      const columns = [...this.querySelectorAll('[data-bike-column]')];
      const groups = [...this.querySelectorAll('[data-compare-group]')];
      const status = this.querySelector('[data-compare-status]');
      const minimum = Math.min(2, choices.length);
      const normalize = (value) => value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();

      const update = () => {
        const selected = new Set(choices.filter((choice) => choice.checked).map((choice) => choice.value));
        choices.forEach((choice) => { choice.disabled = choice.checked && selected.size <= minimum; });
        columns.forEach((column) => { column.hidden = !selected.has(column.dataset.bikeColumn); });
        this.style.setProperty('--compare-count', selected.size);
        this.querySelectorAll('[data-compare-span]').forEach((cell) => { cell.colSpan = selected.size + 1; });

        let visibleRows = 0;
        groups.forEach((group) => {
          let groupRows = 0;
          group.querySelectorAll('[data-compare-row]').forEach((row) => {
            const values = [...row.querySelectorAll('[data-bike-column]')]
              .filter((cell) => selected.has(cell.dataset.bikeColumn))
              .map((cell) => normalize(cell.textContent));
            const same = values.every((value) => value === values[0]);
            row.hidden = differences.checked && same;
            if (!row.hidden) groupRows += 1;
          });
          group.hidden = groupRows === 0;
          visibleRows += groupRows;
        });
        this.querySelector('[data-compare-empty]').hidden = visibleRows > 0;
        status.textContent = `Comparing ${selected.size} bikes. ${differences.checked ? `${visibleRows} differing specifications shown.` : 'All specifications shown.'}`;
        this.scheduleStickyHeader?.();
      };

      controls.addEventListener('change', update, { signal: this.controller.signal });
      update();
      controls.hidden = false;
    }

    setupDragScroll() {
      const scroll = this.querySelector('.bike-compare__scroll');
      if (!scroll || !window.PointerEvent) return;
      const table = scroll.querySelector('table');
      const surfaces = [scroll, this.querySelector('.bike-compare__sticky')].filter(Boolean);
      const desktop = window.matchMedia('(min-width: 990px)');
      const hint = this.querySelector('[data-compare-scroll-hint]');
      const originalHint = hint?.textContent;
      const interactive = 'a, button, input, select, textarea, label, summary, [contenteditable]:not([contenteditable="false"]), [role="button"], [role="link"]';
      const options = { signal: this.controller.signal };
      let drag = null;
      let suppressClick = false;

      const finish = () => {
        const previous = drag;
        drag = null;
        if (previous?.active) suppressClick = true;
        surfaces.forEach((surface) => surface.classList.remove('is-dragging'));
        if (previous?.surface.hasPointerCapture?.(previous.pointerId)) {
          previous.surface.releasePointerCapture(previous.pointerId);
        }
      };
      const updateAvailability = () => {
        const enabled = desktop.matches && scroll.scrollWidth > scroll.clientWidth + 1;
        if (!enabled) finish();
        surfaces.forEach((surface) => surface.classList.toggle('is-draggable', enabled));
        if (hint) hint.textContent = enabled ? 'Click and drag, or scroll sideways to see every bike' : originalHint;
      };
      const start = (event) => {
        if (!desktop.matches || event.pointerType !== 'mouse' || event.button !== 0 || event.isPrimary === false) return;
        if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
        if (scroll.scrollWidth <= scroll.clientWidth + 1 || event.target.closest(interactive)) return;
        // Leave native scrollbar thumbs and borders to the browser.
        if (event.currentTarget === scroll) {
          const rect = scroll.getBoundingClientRect();
          const left = rect.left + scroll.clientLeft;
          const top = rect.top + scroll.clientTop;
          if (event.clientX < left || event.clientX >= left + scroll.clientWidth || event.clientY < top || event.clientY >= top + scroll.clientHeight) return;
        }
        drag = { surface: event.currentTarget, pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: scroll.scrollLeft, active: false };
      };
      const move = (event) => {
        if (!drag || event.pointerId !== drag.pointerId) return;
        if (!(event.buttons & 1)) { finish(); return; }
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.active) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
          if (Math.abs(dx) <= Math.abs(dy)) { finish(); return; }
          drag.active = true;
          surfaces.forEach((surface) => surface.classList.add('is-dragging'));
          drag.surface.setPointerCapture?.(drag.pointerId);
          const selection = window.getSelection();
          if (selection?.anchorNode && drag.surface.contains(selection.anchorNode)) selection.removeAllRanges();
        }
        event.preventDefault();
        scroll.scrollLeft = Math.max(0, Math.min(scroll.scrollWidth - scroll.clientWidth, drag.left - dx));
        this.scheduleStickyHeader?.();
      };
      const end = (event) => { if (drag?.pointerId === event.pointerId) finish(); };

      // A new physical click always remains usable, including immediately after a drag.
      this.addEventListener('pointerdown', () => { suppressClick = false; }, { ...options, capture: true });
      this.addEventListener('click', (event) => {
        if (!suppressClick || event.detail === 0) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      }, { ...options, capture: true });
      surfaces.forEach((surface) => {
        surface.addEventListener('pointerdown', start, options);
        surface.addEventListener('lostpointercapture', end, options);
        surface.addEventListener('dragstart', (event) => {
          if (drag && !event.target.closest(interactive)) event.preventDefault();
        }, options);
      });
      window.addEventListener('pointermove', move, { ...options, passive: false });
      window.addEventListener('pointerup', end, options);
      window.addEventListener('pointercancel', end, options);
      window.addEventListener('blur', finish, options);
      desktop.addEventListener('change', updateAvailability, options);
      const observer = new ResizeObserver(updateAvailability);
      observer.observe(scroll);
      if (table) observer.observe(table);
      updateAvailability();
      this.stopDragScroll = () => {
        finish();
        suppressClick = false;
        observer.disconnect();
        surfaces.forEach((surface) => surface.classList.remove('is-draggable'));
        if (hint) hint.textContent = originalHint;
        this.stopDragScroll = null;
      };
    }

    setupStickyHeader() {
      const scroll = this.querySelector('.bike-compare__scroll');
      const table = scroll?.querySelector('table');
      const headers = [...(table?.querySelectorAll('thead [data-bike-column]') || [])];
      if (!headers.length) return;

      // A separate compact strip can follow page scrolling while the table
      // keeps its horizontal overflow and fixed specification labels.
      const sticky = document.createElement('div');
      sticky.className = 'bike-compare__sticky';
      sticky.setAttribute('role', 'group');
      sticky.setAttribute('aria-label', 'Bike comparison quick links');
      sticky.hidden = true;
      const track = document.createElement('div');
      track.className = 'bike-compare__sticky-track';
      const spacer = document.createElement('div');
      spacer.className = 'bike-compare__sticky-item bike-compare__sticky-spacer';
      track.append(spacer);
      const items = headers.map((header) => {
        const item = document.createElement('div');
        item.className = 'bike-compare__sticky-item';
        ['.bike-compare__image', 'h3', '.bike-compare__price', '.bike-compare__product > .button'].forEach((selector) => {
          const source = header.querySelector(selector);
          if (source) item.append(source.cloneNode(true));
        });
        item.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        item.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
        track.append(item);
        return item;
      });
      const corner = document.createElement('div');
      corner.className = 'bike-compare__sticky-corner';
      corner.textContent = 'Your next ride';
      sticky.append(track, corner);
      this.append(sticky);

      const desktop = window.matchMedia('(min-width: 990px)');
      const siteHeader = document.querySelector('[data-sticky-header]');
      let frame = 0;
      const update = () => {
        frame = 0;
        const viewport = scroll.getBoundingClientRect();
        const top = Math.max(0, siteHeader?.getBoundingClientRect().bottom || 0);
        const visibleHeaders = headers.filter((header) => !header.hidden);
        const titlesPassed = visibleHeaders.length > 0 && visibleHeaders.every((header) =>
          (header.querySelector('h3') || header).getBoundingClientRect().bottom <= top
        );
        if (!titlesPassed || viewport.bottom <= top) {
          sticky.hidden = true;
          return;
        }

        const labelWidth = table.querySelector('.bike-compare__corner').getBoundingClientRect().width;
        spacer.style.width = `${labelWidth}px`;
        corner.style.width = `${labelWidth}px`;
        headers.forEach((header, index) => {
          items[index].hidden = header.hidden;
          if (!header.hidden) items[index].style.width = `${header.getBoundingClientRect().width}px`;
        });
        sticky.style.top = `${top}px`;
        sticky.style.left = `${viewport.left + scroll.clientLeft}px`;
        sticky.style.width = `${scroll.clientWidth}px`;
        track.style.transform = `translateX(${-scroll.scrollLeft}px)`;
        sticky.hidden = false;
        const shift = Math.min(0, viewport.bottom - top - sticky.offsetHeight);
        sticky.style.transform = `translateY(${shift}px)`;
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      this.scheduleStickyHeader = schedule;
      const options = { passive: true, signal: this.controller.signal };
      window.addEventListener('scroll', schedule, options);
      window.addEventListener('resize', schedule, options);
      scroll.addEventListener('scroll', schedule, options);
      desktop.addEventListener('change', schedule, { signal: this.controller.signal });
      sticky.addEventListener('focusin', (event) => {
        sticky.scrollLeft = 0;
        const index = items.findIndex((item) => item.contains(event.target));
        if (index < 0) return;
        const column = headers[index].getBoundingClientRect();
        const viewport = scroll.getBoundingClientRect();
        const left = viewport.left + scroll.clientLeft + corner.offsetWidth;
        const right = viewport.left + scroll.clientLeft + scroll.clientWidth;
        if (column.left < left) scroll.scrollLeft += column.left - left;
        else if (column.right > right) scroll.scrollLeft += column.right - right;
        schedule();
      }, { signal: this.controller.signal });
      const observer = new ResizeObserver(schedule);
      observer.observe(table);
      observer.observe(scroll);
      if (siteHeader) observer.observe(siteHeader);
      this.stopStickyHeader = () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
        sticky.remove();
        this.scheduleStickyHeader = null;
      };
      schedule();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.stopStickyHeader?.();
      this.stopDragScroll?.();
    }
  });
})();
